import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { saveGuestSession } from '@/lib/guestSession';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EligibilityCheck() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hotel');

  // Hotel Guest State
  const [hotelData, setHotelData] = useState({
    confirmation_number: '',
    room_number: '',
    last_name: '',
    email: ''
  });

  // Resident State
  const [residentData, setResidentData] = useState({
    unit_number: '',
    building: '',
    last_name: ''
  });

  const handleHotelValidation = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/validate/hotel-guest`, hotelData);
      if (response.data.valid) {
        toast.success('Validation Successful!', {
          description: `Welcome, ${response.data.guest_name}`
        });
        // Store validation data
        saveGuestSession('hotel_guest', {
          ...response.data,
          room_number: response.data.room_number || hotelData.room_number || null,
          last_name: hotelData.last_name || response.data.last_name || null,
          confirmation_number: response.data.confirmation_number || hotelData.confirmation_number || null
        }, response.data.guest_token, response.data.guest_token_expires_at);
        navigate('/book');
      }
    } catch (error) {
      toast.error('Validation Failed', {
        description: error.response?.data?.detail || 'Please check your information and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResidentValidation = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/validate/resident`, residentData);
      if (response.data.valid) {
        toast.success('Validation Successful!', {
          description: `Welcome back, ${response.data.resident.first_name}`
        });
        // Store validation data
        saveGuestSession('resident', response.data.resident, response.data.guest_token, response.data.guest_token_expires_at);
        navigate('/book');
      }
    } catch (error) {
      toast.error('Validation Failed', {
        description: error.response?.data?.detail || 'Resident not found. Please check your information.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] py-12 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-12">
        <div className="flex items-center justify-center mb-8" data-testid="logo">
          <span className="font-serif tracking-wide whitespace-nowrap text-3xl md:text-4xl text-[#1C1917]" data-testid="brand-name">Acqualina Resort</span>
        </div>

        <div className="text-center">
          <h2 className="text-4xl md:text-5xl font-serif tracking-tight text-[#1C1917] mb-4" data-testid="page-title">
            Check Your Eligibility
          </h2>
          <p className="text-lg text-[#78716C] leading-relaxed">
            Verify your status as a hotel guest or resident to continue with your reservation.
          </p>
        </div>
      </div>

      {/* Eligibility Form */}
      <div className="max-w-2xl mx-auto">
        <Card className="bg-white border-[#E7E5E4] shadow-sm" data-testid="eligibility-card">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="hotel" data-testid="tab-hotel">Hotel Guest</TabsTrigger>
                <TabsTrigger value="resident" data-testid="tab-resident">Condo Resident</TabsTrigger>
              </TabsList>

              {/* Hotel Guest Form */}
              <TabsContent value="hotel">
                <form onSubmit={handleHotelValidation} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="confirmation_number">Confirmation Number</Label>
                    <Input
                      id="confirmation_number"
                      placeholder="Enter your confirmation number"
                      value={hotelData.confirmation_number}
                      onChange={(e) => setHotelData({ ...hotelData, confirmation_number: e.target.value, room_number: '' })}
                      className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                      data-testid="input-confirmation-number"
                    />
                  </div>

                  <div className="text-center text-sm text-[#78716C]">OR</div>

                  <div className="space-y-2">
                    <Label htmlFor="room_number">Room Number</Label>
                    <Input
                      id="room_number"
                      placeholder="Enter your room number"
                      value={hotelData.room_number}
                      onChange={(e) => setHotelData({ ...hotelData, room_number: e.target.value, confirmation_number: '' })}
                      className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                      data-testid="input-room-number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      placeholder="Enter your last name"
                      value={hotelData.last_name}
                      onChange={(e) => setHotelData({ ...hotelData, last_name: e.target.value })}
                      required
                      className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                      data-testid="input-last-name"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || (!hotelData.confirmation_number && !hotelData.room_number)}
                    className="w-full bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-8 py-6 font-serif tracking-wide transition-all duration-300 shadow-md hover:shadow-xl disabled:opacity-50"
                    data-testid="btn-validate-hotel"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Validating...</>
                    ) : (
                      'Validate & Continue'
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Resident Form */}
              <TabsContent value="resident">
                <form onSubmit={handleResidentValidation} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="unit_number">Unit Number *</Label>
                    <Input
                      id="unit_number"
                      placeholder="Enter your unit number"
                      value={residentData.unit_number}
                      onChange={(e) => setResidentData({ ...residentData, unit_number: e.target.value })}
                      required
                      className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                      data-testid="input-unit-number"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="building">Building (Optional)</Label>
                    <Input
                      id="building"
                      placeholder="Enter your building"
                      value={residentData.building}
                      onChange={(e) => setResidentData({ ...residentData, building: e.target.value })}
                      className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                      data-testid="input-building"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="resident_last_name">Last Name *</Label>
                    <Input
                      id="resident_last_name"
                      placeholder="Enter your last name"
                      value={residentData.last_name}
                      onChange={(e) => setResidentData({ ...residentData, last_name: e.target.value })}
                      required
                      className="bg-[#FAFAF9] border-0 border-b border-[#A8A29E] rounded-none focus:ring-0 focus:border-[#9F1239] px-0 py-4"
                      data-testid="input-resident-last-name"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#9F1239] hover:bg-[#881337] text-white rounded-none px-8 py-6 font-serif tracking-wide transition-all duration-300 shadow-md hover:shadow-xl"
                    data-testid="btn-validate-resident"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Validating...</>
                    ) : (
                      'Validate & Continue'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#0F766E] hover:text-[#9F1239] hover:bg-transparent font-medium"
            data-testid="btn-back-home"
          >
            ← Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
